/**
 * Advance Settlement Reports — Stage 73.20w Phase 2.
 *
 * 8 reports as CSV exports filtered from db.loans + db.expense_claim_sheets.
 * Single backend endpoint serves all reports — no duplicate logic.
 */
import React, { useEffect, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileBarChart, Download, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdvanceSettlementReports() {
  const [activeCompany] = useActiveCompany();
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    axios.get(`${API}/advance-settlements/reports/list`, { withCredentials: true })
      .then(r => setReports(r.data?.reports || []))
      .catch(() => toast.error('Failed to load reports'));
  }, []);

  const download = async (kind) => {
    setBusy(b => ({ ...b, [kind]: true }));
    try {
      const params = new URLSearchParams();
      if (activeCompany) params.set('company_id', activeCompany);
      const r = await axios.get(`${API}/advance-settlements/reports/${kind}.csv?${params}`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `advance_${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${kind} report`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(b => ({ ...b, [kind]: false }));
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="advance-reports-page">
        <Link to="/advance-settlements" className="text-sm text-amber-700 hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Advance Settlements
        </Link>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <FileBarChart className="w-7 h-7 text-amber-600" />
          Advance Settlement Reports
        </h1>
        <p className="text-sm text-slate-600 max-w-3xl">
          All 8 reports are filtered CSV exports computed from <code className="text-xs bg-slate-100 px-1 rounded">db.loans</code> + <code className="text-xs bg-slate-100 px-1 rounded">db.expense_claim_sheets</code> — single source, no duplicate logic.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {reports.map(r => (
            <Card key={r.key} className="p-4 hover:shadow-md transition" data-testid={`advance-report-card-${r.key}`}>
              <div className="text-sm font-semibold text-slate-800 mb-3">{r.label}</div>
              <Button
                size="sm"
                onClick={() => download(r.key)}
                disabled={busy[r.key]}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                data-testid={`advance-download-${r.key}`}
              >
                {busy[r.key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                Download CSV
              </Button>
            </Card>
          ))}
        </div>
      </div>
    </MainLayout>
  );
}
